import React, { useCallback, useMemo } from 'react';

import { z } from 'zod';

import { AuthActionsContext } from './AuthActionsContext';
import { type AuthActionsProviderProps, useAuthActionsValue } from './AuthActionsProvider';
import {
  AuthContext,
  type AuthContextState,
  type LoginCredentials,
  type LoginResult,
} from './AuthContext';
import { DEFAULT_LOGIN_DISPLAY_ERROR_MESSAGE } from './ctx/initAuthContext';
import { type SessionLoader, useSession } from './ctx/useSession';
import type { TrackError } from './helpers';
import { loginWithPasswordlessToken as loginWithPasswordlessTokenRequest } from './sessionClient';

export type AuthSessionClient = Pick<
  AuthContextState,
  'validateResetPasswordToken' | 'resetPassword'
> & { loadSession: SessionLoader };

export type AuthSessionContextProviderProps = AuthActionsProviderProps & {
  client: AuthSessionClient;
  /** Replaces Rails sign-out when the consuming application owns the active session. */
  onLogout?: AuthContextState['logout'];
};

const loginErrorSchema = z.object({ error: z.string() });

export const AuthSessionContextProvider: React.FC<AuthSessionContextProviderProps> = ({
  trackError,
  client,
  onLogout,
  fetch: fetchFn = fetch,
  authBaseUrl,
  analyticsPrefix,
  recordMarketingSmsConsent: writeMarketingSmsConsent,
  children,
}) => {
  const resolvedAuthBaseUrl = authBaseUrl.replace(/\/+$/, '');

  const { authentication, refresh } = useSession(client.loadSession, trackError);
  const { validateResetPasswordToken, resetPassword } = client;

  const logout = useCallback(async () => {
    try {
      const response = await fetchFn(`${resolvedAuthBaseUrl}/session/sign-out`, {
        method: 'GET',
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Sign-out request failed');
      window.location.reload();
    } catch {
      trackError?.('logout', new Error('Sign-out request failed'));
      window.location.assign(`${resolvedAuthBaseUrl}/logout`);
    }
  }, [fetchFn, resolvedAuthBaseUrl, trackError]);

  const verifyLogin: AuthContextState['verifyLogin'] = useCallback(
    (credentials) => callVerifyLogin(resolvedAuthBaseUrl, fetchFn, trackError, credentials),
    [fetchFn, resolvedAuthBaseUrl, trackError],
  );

  const loginWithPasswordlessToken: AuthContextState['loginWithPasswordlessToken'] = useCallback(
    (params) => loginWithPasswordlessTokenRequest(resolvedAuthBaseUrl, params, fetchFn),
    [fetchFn, resolvedAuthBaseUrl],
  );

  const actions = useAuthActionsValue({
    authBaseUrl,
    analyticsPrefix,
    fetch: fetchFn,
    trackError,
    recordMarketingSmsConsent: writeMarketingSmsConsent,
  });

  const value = useMemo<AuthContextState>(
    () => ({
      ...actions,
      authentication,
      logout: onLogout ?? logout,
      refresh,
      verifyLogin,
      validateResetPasswordToken,
      resetPassword,
      loginWithPasswordlessToken,
    }),
    [
      actions,
      authentication,
      logout,
      onLogout,
      refresh,
      verifyLogin,
      validateResetPasswordToken,
      resetPassword,
      loginWithPasswordlessToken,
    ],
  );

  return (
    <AuthActionsContext.Provider value={actions}>
      <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
    </AuthActionsContext.Provider>
  );
};

async function callVerifyLogin(
  authBaseUrl: string,
  fetchFn: typeof fetch,
  trackError: TrackError | undefined,
  credentials: LoginCredentials,
): Promise<LoginResult> {
  const payload =
    'googleIdToken' in credentials
      ? { google_id_token: credentials.googleIdToken }
      : 'facebookIdToken' in credentials
        ? { facebook_id_token: credentials.facebookIdToken }
        : { human: { email: credentials.email, password: credentials.password } };

  let resp: Response;
  try {
    resp = await fetchFn(`${authBaseUrl}/verify_login`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
  } catch {
    trackError?.('login', new Error('Login verification request failed'));
    return { success: false, displayErrorMessage: DEFAULT_LOGIN_DISPLAY_ERROR_MESSAGE };
  }

  if (resp.ok) return { success: true };

  let displayErrorMessage = DEFAULT_LOGIN_DISPLAY_ERROR_MESSAGE;
  try {
    const parsed = loginErrorSchema.safeParse(await resp.json());
    if (parsed.success) displayErrorMessage = parsed.data.error;
  } catch {
    trackError?.(
      'login error without error message',
      new Error('Invalid login verification response'),
    );
  }
  return { success: false, displayErrorMessage };
}
